import { useEffect } from "react";
import "@/App.css";
import SiteHeader from "@/components/SiteHeader";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ArrowLeft, Mail, Phone, MapPin } from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.05 + i * 0.08, ease } }),
};

const Reveal = ({ i = 0, className = "", children, ...rest }) => (
  <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: "-60px" }} className={className} {...rest}>
    {children}
  </motion.div>
);

const ADDRESSES = [
  "Communication difficulties", "Recurring conflict", "Emotional disconnection",
  "Trust and betrayal", "Boundaries with extended family", "Cultural and religious differences",
  "Intimacy concerns", "Marriage transitions", "Parenting disagreements", "Separation and reconciliation",
];

const Hero = () => (
  <section className="px-6 md:px-14 pt-14 md:pt-20 pb-12 max-w-6xl">
    <div className="md:grid md:grid-cols-12 md:gap-12 items-end">
      <div className="md:col-span-7">
        <motion.a variants={fadeUp} initial="hidden" animate="visible" custom={0} href="/" className="inline-flex items-center gap-2 text-sm text-[#2c1f20]/60 hover:text-[#6e4550] transition-colors">
          <ArrowLeft size={14} /> All services
        </motion.a>
        <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={1} className="eyebrow mt-8">Services · Couples</motion.p>
        <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={2} data-testid="couples-heading"
          className="font-serif-display mt-4 text-[clamp(2.6rem,6vw,4.5rem)] leading-[1.05] font-medium">
          Couples <em className="text-[#6e4550]">Psychotherapy.</em>
        </motion.h1>
        <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mt-5 text-sm font-semibold tracking-[0.18em] uppercase text-[#2c1f20]/70">
          Clinical Psychotherapy for Adults, Couples, Families &amp; Youth
        </motion.p>
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4} className="mt-6 space-y-4 text-[#2c1f20]/75 leading-relaxed max-w-2xl">
          <p>
            Relationships can become strained by unresolved conflict, communication difficulties, differences in
            expectations, trust concerns, family involvement, or changes in the relationship over time.
          </p>
          <p>
            Couples therapy provides a structured environment to understand recurring interactional patterns and
            develop healthier ways of communicating and responding to one another.
          </p>
        </motion.div>
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={5} className="mt-8 flex flex-wrap gap-4">
          <a data-testid="couples-book-button" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book Initial Consultation <ArrowRight size={15} /></a>
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4, ease }}
        className="mt-12 md:mt-0 md:col-span-5">
        <div className="rounded-md overflow-hidden">
          <img src="https://images.unsplash.com/photo-1604881991720-f91add269bed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxzdWJ0bGUlMjBjb3VwbGUlMjBoYW5kcyUyMHRoZXJhcHl8ZW58MHx8fHwxNzkwMjY2MjY5fDA&ixlib=rb-4.1.0&q=85"
            alt="Two people's hands resting together on a table" data-testid="couples-artwork"
            className="w-full h-[320px] md:h-[420px] object-cover" />
        </div>
      </motion.div>
    </div>
  </section>
);

const TreatmentList = () => (
  <section data-testid="couples-treatment-list" className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10 bg-[#f1ece4]/60">
    <Reveal><p className="eyebrow">Treatment may address</p></Reveal>
    <div className="grid sm:grid-cols-2 gap-x-12 gap-y-3.5 mt-8 max-w-4xl">
      {ADDRESSES.map((t, i) => (
        <Reveal key={t} i={i % 4} className="flex gap-3 text-[#2c1f20]/80">
          <span className="text-[#8a5a63] mt-0.5">✦</span><span>{t}</span>
        </Reveal>
      ))}
    </div>
  </section>
);

const Context = () => (
  <section className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10 max-w-4xl">
    <Reveal><p className="eyebrow">Our approach</p></Reveal>
    <Reveal i={1}>
      <h2 className="font-serif-display text-3xl md:text-4xl mt-4 leading-tight">A space that understands <em className="text-[#6e4550]">your context.</em></h2>
    </Reveal>
    <Reveal i={2}>
      <div className="mt-6 space-y-4 text-[#2c1f20]/75 leading-relaxed">
        <p>
          Our couples work is grounded in the Gottman Method — one of the most researched approaches to couples
          therapy — and is offered with particular attention to the realities many South Asian, Muslim, and
          intercultural couples carry: in-law involvement, extended family expectations, and community pressures
          neither partner chose.
        </p>
        <p>
          Sessions are available in English and Urdu, and we can move between languages whenever it helps.
        </p>
      </div>
    </Reveal>
  </section>
);

const SA_TOPICS = [
  ["In-Law & Extended Family Dynamics", "Work through involvement from parents and in-laws — setting boundaries as a couple while respecting the family systems you both belong to."],
  ["Intergenerational & Cultural Expectations", "Navigate the pressure of family expectations around marriage, roles, children, and career — without either partner carrying it alone."],
  ["Intercultural & Interfaith Relationships", "Support for couples bridging different cultures, faiths, or denominations — building shared meaning out of difference."],
  ["Love & Arranged Marriage Transitions", "Whether your marriage began through family introduction or your own choosing, early marriage brings adjustments worth working through with structure."],
];

const SouthAsian = () => (
  <section id="south-asian-couples" data-testid="south-asian-section" className="scroll-mt-28 px-6 md:px-14 py-20 md:py-28 border-t border-[#2c1f20]/10 bg-[#eae2d6]/60">
    <Reveal><p className="eyebrow">A dedicated focus</p></Reveal>
    <Reveal i={1}>
      <h2 className="font-serif-display text-3xl md:text-5xl mt-4 max-w-3xl leading-tight">South Asian <em className="text-[#6e4550]">Couples Therapy.</em></h2>
    </Reveal>
    <Reveal i={2}>
      <div className="mt-6 space-y-4 text-[#2c1f20]/75 leading-relaxed max-w-3xl">
        <p>
          For many South Asian couples, a relationship is never just two people. It carries extended families,
          community expectations, faith, and histories that span generations — and standard couples therapy often
          misses that context entirely.
        </p>
        <p>
          At Haya Healing Psychotherapy, these dynamics are understood from the inside. The practice offers couples therapy in
          English and Urdu that treats your cultural reality as central to the work — not as something to explain
          first. Grounded in the Gottman Method, sessions get specific about how conflict starts, where
          communication breaks down, and how you find your way back to each other.
        </p>
      </div>
    </Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
      {SA_TOPICS.map(([t, d], i) => (
        <Reveal key={t} i={i + 2} className="card">
          <h3 className="font-serif-display text-lg leading-snug">{t}</h3>
          <p className="text-xs text-[#2c1f20]/70 mt-2 leading-relaxed">{d}</p>
        </Reveal>
      ))}
    </div>
    <Reveal i={3} className="mt-10">
      <a data-testid="south-asian-book-button" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book a Couples Consultation <ArrowRight size={15} /></a>
    </Reveal>
    <p className="mt-8 text-xs text-[#2c1f20]/50 leading-relaxed max-w-4xl">
      South Asian couples therapy in Milton &amp; Burlington — serving Mississauga, Brampton, Oakville, Halton Region, and the GTA, with virtual sessions across Ontario. Sessions in English and Urdu, led by Momna Sajid, Registered Psychotherapist (Qualifying), CRPO.
    </p>
  </section>
);

const Logistics = () => (
  <section className="border-y border-[#2c1f20]/10">
    <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#2c1f20]/10">
      {[
        ["Virtual", "Across Ontario, on a secure PHIPA-compliant platform"],
        ["In-person", "Halton Medix Walk-In Clinic — Milton & Burlington"],
        ["Complimentary 15 minutes", "A no-pressure consultation to see if we're the right fit"],
      ].map(([t, d]) => (
        <div key={t} className="py-7 px-6 text-center">
          <p className="font-serif-display text-2xl text-[#6e4550]">{t}</p>
          <p className="text-xs text-[#2c1f20]/55 mt-1">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Cta = () => (
  <footer className="px-6 md:px-14 py-16 md:py-24 bg-[#f1ece4]/60">
    <Reveal><p className="eyebrow">Begin here</p></Reveal>
    <Reveal i={1}>
      <h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl leading-tight">
        Start with a complimentary <em className="text-[#6e4550]">15-minute consultation.</em>
      </h2>
    </Reveal>
    <Reveal i={2} className="mt-8 flex flex-wrap gap-6 items-center">
      <a data-testid="couples-footer-book" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book via Jane App <ArrowUpRight size={15} /></a>
      <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Mail size={15} /> momna@hayahealingpsychotherapy.com</a>
      <a href="tel:+13656172135" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Phone size={15} /> (365) 617-2135</a>
    </Reveal>
    <p className="mt-14 text-xs text-[#2c1f20]/40 flex items-center gap-2"><MapPin size={12} /> Milton · Burlington · Virtual across Ontario</p>
        <p className="mt-4 text-[8px] leading-relaxed text-[#2c1f20]/30">Some content on this site was created with the assistance of AI tools and reviewed by Haya Healing Psychotherapy.</p>
  </footer>
);

export default function Couples() {
  useEffect(() => {
    document.title = "Couples Therapy in Milton & Burlington | Haya Healing Psychotherapy";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Couples and marriage counselling grounded in the Gottman Method — including South Asian couples therapy in English & Urdu. In person in Milton & Burlington, virtual across Ontario.");
  }, []);
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <Hero />
      <TreatmentList />
      <Context />
      <SouthAsian />
      <Logistics />
      <Cta />
    </div>
  );
}
